import React, { useState } from "react";
import {
	FormField,
	FormItem,
	FormMessage,
	Form,
	FormLabel,
	FormControl,
    FormDescription,
} from "../../components/ui/form";
import { Card } from "../../components/ui/card";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { changePasswordSchema, verifyOTPSchema } from "./validators";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { ClipLoader } from "react-spinners";
import img from "../../pics/landImg.jpeg";
import { useCreateNewPassword, useVerifyOtp } from "../../hooks/auth";
import { useToast } from "../../components/ui/use-toast";
import { useNavigate } from "react-router-dom";

const ChangePassword = () => {
	const { toast } = useToast();

	const navigate = useNavigate();
    const [showChangePassword, setShowChangePassword] = useState(false)

	const { newPassword, isLoading: isCreating } = useCreateNewPassword({
		onSuccess: () => {
			toast({
				variant: "success",
				title: "Success",
				description: `${"Password changed successfully, please login"}`,
			});

            setTimeout(() => {
				navigate("/");
			}, 3000);
		},
		onError: (error) => {
			toast({
				variant: "destructive",
				title: "Error",
				description: `${error?.response?.data?.message}`,
			});
		},
	});

	const {
		verify,
		isLoading: isVerifying,
	} = useVerifyOtp({
		onSuccess: () => {
			toast({
				variant: "success",
				title: "Success",
				description: `${"Verification successfull, create a new password"}`,
			});
            setShowChangePassword(true)
		},
		onError: (error) => {
			if (error?.response?.data?.message === "Expired OTP") {
                toast({
					variant: "destructive",
					title: "Error",
					description: `OTP has expired, please reset password again`,
				});
				setTimeout(() => {
					navigate("/forgot-password");
				}, 3000);
			} else {
				toast({
					variant: "destructive",
					title: "Error",
					description: `${error?.response?.data?.message}`,
				});
				setTimeout(() => {
					navigate("/");
				}, 3000);
			}
		},
	});

	const form = useForm({
		resolver: zodResolver(changePasswordSchema),
		defaultValues: {
			email: "",
			password: "",
		},
	});

    const verifyForm = useForm({
		resolver: zodResolver(verifyOTPSchema),
		defaultValues: {
			email: "",
			otp: "",
		},
	});

	function onSubmit(values) {
		newPassword(values);
	}

	function onVerify(values) {
		verify(values);
	}

	return (
		<section className="w-full h-full">
			<div className=" pl-24  pt-10">
				<img src="/sproxillogo.png" alt="" className="w-[120px]" />
			</div>

			{!showChangePassword && (
				<div className="flex flex-col">
					<div className="w-full h-[80vh] flex flex-col gap-[10px] justify-center items-center">
						<Card className=" h-[fit-content] w-[350px] p-[20px] min-w-[230px]">
							<h1 className="font-extrabold text-xl">
								Verify it is you{" "}
							</h1>
							<h4 className=" text-xs">
								Enter your email and OTP below
							</h4>
							<br />
							<Form {...verifyForm}>
								<form
									onSubmit={verifyForm.handleSubmit(onVerify)}
									className="space-y-8"
								>
									<FormField
										control={verifyForm.control}
										name="email"
										render={({ field }) => (
											<FormItem>
												<FormLabel>Email</FormLabel>
												<FormControl>
													<Input
														placeholder="johndoe@gmail.com"
														{...field}
													/>
												</FormControl>
												<FormDescription>
													This is your registered
													email.
												</FormDescription>
												<FormMessage />
											</FormItem>
										)}
									/>

									<FormField
										control={verifyForm.control}
										name="otp"
										render={({ field }) => (
											<FormItem>
												<FormLabel>OTP</FormLabel>
												<FormControl>
													<Input
														placeholder="1111"
														{...field}
													/>
												</FormControl>
												<FormDescription>
													Enter the otp sent to your
													email.
												</FormDescription>
												<FormMessage />
											</FormItem>
										)}
									/>

									<Button
										type="submit"
										variant=""
										disabled={isVerifying}
									>
										{isVerifying ? (
											<span className="mx-[20px]">
												<ClipLoader
													color="primary"
													size={20}
												/>
											</span>
										) : (
											<span> Verify</span>
										)}
									</Button>
								</form>
							</Form>
						</Card>
					</div>
				</div>
			)}

			<div className="flex flex-col">
				{showChangePassword && (
					<>
						<div className="w-full h-[80vh] flex flex-col gap-[10px] justify-center items-center">
							<Card className=" h-[fit-content] w-[350px] p-[20px] min-w-[230px]">
								<h1 className="font-extrabold text-xl">
									Change Your Password{" "}
								</h1>
								<h4 className=" text-xs">
									Try not to use previous passwords
								</h4>
								<br />
								<Form {...form}>
									<form
										onSubmit={form.handleSubmit(onSubmit)}
										className="space-y-8"
									>
										<FormField
											control={form.control}
											name="email"
											render={({ field }) => (
												<FormItem>
													<FormLabel>Email</FormLabel>
													<FormControl>
														<Input
															placeholder="johndoe@gmail.com"
															{...field}
														/>
													</FormControl>
													<FormDescription>
														This is your registered
														email.
													</FormDescription>
													<FormMessage />
												</FormItem>
											)}
										/>
										<FormField
											control={form.control}
											name="password"
											render={({ field }) => (
												<FormItem>
													<FormLabel>
														Password
													</FormLabel>
													<FormControl>
														<Input
															placeholder=""
															{...field}
															type="password"
															value={undefined}
															isPassword
															autoComplete="password"
														/>
													</FormControl>
													<FormMessage />
												</FormItem>
											)}
										/>

										<Button
											type="submit"
											variant=""
											disabled={isCreating}
										>
											{isCreating ? (
												<span className="mx-[20px]">
													<ClipLoader
														color="primary"
														size={20}
													/>
												</span>
											) : (
												<span> Submit</span>
											)}
										</Button>
									</form>
								</Form>
							</Card>
							<div className=" text-primary text-[9px]">
								Copyright &copy; {new Date().getFullYear()}
							</div>
						</div>

						<div className="h-[5vh] flex items-end w-full justify-center">
							<img src={img} alt="" className=" md:w-[65%] " />
						</div>
					</>
				)}
			</div>
		</section>
	);
};

export default ChangePassword;
